import { format, UTCDate } from '@/utils/dates';
import { Currency } from '@seapoint/server/enums';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '../../../design-system/button/button';
import { DataTable } from '../../../design-system/data-table/data-table';
import { Stack } from '../../../design-system/stack/stack';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';
import type { BankFeedConnection } from './bank-feeds-table-column-definition';

const DATE_FORMAT = 'dd MMM yyyy';

const columnHelper = createColumnHelper<BankFeedConnection>();

const availableFromXeroTableColumnDefinition = [
  columnHelper.accessor('externalAccountName', {
    header: 'Xero account',
    cell: ({ row }) => {
      const name = row.original.externalAccountName;
      const currency = row.original.currency as Currency;
      const balance = row.original.externalAccountBalance;

      const balanceText =
        balance != null ? getAmountWithCurrency({ amount: balance, currency, isAccountBalance: true }) : undefined;

      return (
        <Stack space={0.5}>
          <p className="text-sm text-black">{name}</p>
          <p className="text-xs text-muted-foreground">{balanceText ?? currency}</p>
        </Stack>
      );
    },
    size: 50,
  }),
  columnHelper.accessor('lastSyncDate', {
    header: 'Last sync',
    cell: ({ getValue, row }) => {
      const lastSyncDate = getValue();
      const isConnected = row.original.isConnected;

      // Only show sync date for imported accounts
      if (!isConnected || !lastSyncDate) {
        return null;
      }

      const formatted = format(new UTCDate(lastSyncDate), DATE_FORMAT);
      return <p className="text-sm text-black">{formatted}</p>;
    },
    size: 30,
  }),
  columnHelper.display({
    id: 'actions',
    cell: ({ row, table }) => {
      const onImport = table.options.meta?.onImport;
      const onXeroDisconnect = table.options.meta?.onXeroDisconnect;
      const isLoading = table.options.meta?.isXeroLoading;
      const loadingId = table.options.meta?.xeroLoadingId;
      const isConnected = row.original.isConnected;
      const isThisRowLoading = loadingId === row.original.id;

      return (
        <div className="flex justify-end">
          <Button
            variant="link"
            size="default"
            onClick={() => {
              if (isConnected) {
                onXeroDisconnect?.(row.original.id, row.original.externalAccountName, row.original.currency);
              } else {
                // Extract the actual Xero account ID (remove 'xero-' prefix)
                const xeroAccountId = row.original.id.replace('xero-', '');
                onImport?.(xeroAccountId);
              }
            }}
            disabled={isLoading}
            isLoading={isThisRowLoading}
            className={isConnected ? 'text-destructive' : 'text-link'}
          >
            {isConnected ? 'Disconnect' : 'Import'}
          </Button>
        </div>
      );
    },
    size: 20,
    minSize: 15,
  }),
];

type AvailableFromXeroTableProps = {
  connections: BankFeedConnection[];
  onImport: (xeroAccountId: string) => void;
  onDisconnect: (id: string, accountName: string, currency: string) => void;
  isLoading?: boolean;
  loadingId?: string | null;
};

export const AvailableFromXeroTable = ({
  connections,
  onImport,
  onDisconnect,
  isLoading = false,
  loadingId = null,
}: AvailableFromXeroTableProps) => {
  return (
    <DataTable
      columns={availableFromXeroTableColumnDefinition}
      data={connections}
      meta={{
        onImport,
        onXeroDisconnect: onDisconnect,
        isXeroLoading: isLoading,
        xeroLoadingId: loadingId,
      }}
    />
  );
};
